/**
 * Per-section persistence gating + dedup (Feature: redes-sociais-report, Task 7.3).
 *
 * Requirement 6.6: when a section receives its `completed` Section_Event the
 * frontend persists that section ENCRYPTED, deduplicated by `user_reports_id`
 * (and section id). `started`/`report_section` events are ephemeral preview and
 * MUST NOT persist (Requirement 6.4, design D3).
 *
 * The decision is kept here as pure functions (no React, no crypto, no I/O) so
 * the dedup/gating contract can be unit-tested directly. The actual encryption
 * and persistence live in {@link useSectionPersistence}.
 *
 * Dedup is layered on the existing per-id idempotency primitives
 * (`~/helpers/reportSaveDedup.helper`) used by `PendingReportSockets` for the
 * legacy report-save branch — the only difference is the COMPOSITE key
 * (`user_reports_id` + `section_id`), so that:
 *   - the section's initial (non-incremental) `completed` persists exactly once, and
 *   - a duplicate / re-sent initial `completed` for the SAME section never double-persists.
 *
 * Incremental ("Buscar mais dados") completions are EXEMPT from that
 * registry — they share the same `section_id` as the initial completion but
 * each one is a distinct, always-persistable delivery (see the
 * `event.incremental` branch below).
 */
import {
  markSaveIssued,
  releaseSave,
  shouldIssueSave,
} from "~/helpers/reportSaveDedup.helper";
import { SectionEvent } from "~/types/section-streaming";

/**
 * Build the composite dedup key for a section.
 *
 * Keyed by `(user_reports_id, section_id)` so concurrent distinct sections /
 * reports are never dropped — only the exact section already in flight / done
 * for a given report is blocked.
 */
export function buildSectionDedupKey(
  userReportsId: string,
  sectionId: string,
): string {
  return `${userReportsId}::${sectionId}`;
}

/**
 * Decide whether a parsed Section_Event should trigger a (deduped) persist.
 *
 * Returns `true` iff ALL of the following hold:
 *  - the event is `completed` (never `started` / `report_section` / `error`),
 *  - it carries a non-empty `user_reports_id` (the pending report exists),
 *  - it carries section content to persist (`section` is not null/undefined),
 *  - the `(user_reports_id, section_id)` key is NOT already in the registry
 *    (first persist for this section, not a re-send during the in-flight window
 *    or after success).
 *
 * @param event - The parsed Section_Event from the status WebSocket.
 * @param registry - Set of composite keys already persisted / in flight.
 */
export function shouldPersistCompletedSection(
  event: SectionEvent,
  registry: Set<string>,
): boolean {
  if (event.event !== "completed") {
    return false;
  }

  const userReportsId = event.user_reports_id;
  if (!userReportsId) {
    return false;
  }

  // Only `completed` events that actually carry the section payload are
  // persistable; a content-less `completed` has nothing to encrypt and store.
  if (event.section === undefined || event.section === null) {
    return false;
  }

  // Incremental ("Buscar mais dados") results share the SAME section_id as
  // the initial streaming preview's own `completed` event — found live
  // (2026-08-13): the initial preview's completion permanently marked that
  // composite key as persisted, so the registry silently blocked every
  // subsequent fetch-more persist for that section for the rest of the
  // session (the initial-load path is the only one that legitimately
  // persists exactly once; each fetch-more click is a distinct, always-
  // persistable delivery, deduped separately below).
  if (event.incremental === true) {
    return true;
  }

  return shouldIssueSave(
    buildSectionDedupKey(userReportsId, event.section_id),
    registry,
  );
}

/**
 * Mark a section as persisted (in flight or done) so re-sent `completed` events
 * are blocked. Call BEFORE issuing the encryption/persist work.
 */
export function markSectionPersisted(
  userReportsId: string,
  sectionId: string,
  registry: Set<string>,
): void {
  markSaveIssued(buildSectionDedupKey(userReportsId, sectionId), registry);
}

/**
 * Release a section key so a legitimate retry can re-persist. Call on persist
 * error only — a successful persist keeps the key to block poll-cycle re-sends.
 */
export function releaseSectionPersist(
  userReportsId: string,
  sectionId: string,
  registry: Set<string>,
): void {
  releaseSave(buildSectionDedupKey(userReportsId, sectionId), registry);
}
